// File: Componates/CheckOutPrintInvoice.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux'

import "../Invoice.css";
import { GetInvoiceById } from "adminApi/RoomCheCkOutApi/RoomCheCkOut";
import { formatValue } from "./utils/checkoutTableUtils";

// Parse a currency-ish string like "₹1,142.86" into a plain number.
const parseAmount = (val) => parseFloat((val ?? "0").toString().replace(/[^\d.-]/g, "")) || 0;

// Format a number back into the same "₹1,142.86" style used elsewhere.
const formatCurrency = (n) =>
  `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CheckOutPrintInvoice = ({ id, onLoaded }) => {

  const user = useSelector((state) => state.user);

  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await GetInvoiceById(id);
        // adjust `.data` if your API wrapper nests differently
        const data = res?.data?.data || [];
        setInvoiceData(data);
        onLoaded?.();
      } catch (err) {
        console.error("Failed to load invoice", err);
        setError("Failed to load invoice.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchInvoice();
  }, [id]);

  if (loading) {
    return <div className="invoice-wrapper invoice-print-root">Loading invoice...</div>;
  }

  if (error || !invoiceData) {
    return (
      <div className="invoice-wrapper invoice-print-root">
        {error || "Invoice not found."}
      </div>
    );
  }

  // Fold parking into the Room Charges total for display, so Parking no
  // longer shows as its own line item on the printed invoice.
  // Fold parking into the Room Charges total for display, so Parking no
  // longer shows as its own line item on the printed invoice.
  const roomChargesWithParking =
    parseAmount(invoiceData?.summary?.room_charges_total) +
    parseAmount(invoiceData?.summary?.parking_charges_total);

  // Same merge, but for the itemized Description table: find the parking
  // line, then split its amount evenly across every room-charge line
  // (unit === "nights") and add each room's share to that room's own
  // Amount (and Rate, so the two columns stay in sync). Drop the parking
  // line afterwards.
  const parkingChargeItem = (invoiceData?.charges || []).find((c) =>
    c.description?.toLowerCase().includes("parking")
  );
  const parkingChargeAmount = parkingChargeItem ? parseAmount(parkingChargeItem.amount) : 0;

  const roomChargeItems = (invoiceData?.charges || []).filter(
    (c) => c.unit?.toLowerCase() === "nights"
  );
  const roomCount = roomChargeItems.length || 1;
  const parkingPerRoom = parkingChargeAmount / roomCount;

  const displayCharges = (invoiceData?.charges || [])
    .filter((c) => c !== parkingChargeItem)
    .map((c) => {
      const isRoomLine = c.unit?.toLowerCase() === "nights";
      if (isRoomLine && parkingChargeAmount > 0) {
        const mergedAmount = formatCurrency(parseAmount(c.amount) + parkingPerRoom);
        // Keep Rate and Amount in sync (qty is 1 night) so the parking
        // share doesn't show as a mismatch between the two columns.
        return { ...c, rate: mergedAmount, amount: mergedAmount };
      }
      return c;
    });

  return (
    <div className="invoice-wrapper invoice-print-root">
      {/* ---------------------------------------------------------------- */}
      {/* Header                                                         */}
      {/* ---------------------------------------------------------------- */}
      <header className="pb-2 mb-2 border-bottom">
        <div className="d-flex justify-content-center align-items-center w-100">
          <div className="pb-3">
            <img
              src={user.logo}
              alt="Logo"
              width={50}
              height={50}
              className="me-1"
            />
          </div>


          <h1 className="hotel-name fw-bold mb-0">
            SHRI PATIDAR SEVA SAMAJ
          </h1>
        </div>
      </header>

      <section className="mb-3">
        <div className="text-dark small w-100 text-center">
          Trust Regd No. A2689 (BOM) Dated 25.11.1964 | Bombay Public Act. 1950<br />
          Naya Road, Near Bus Stand, Nathdwara, Dist Rajsamand, Rajasthan - 313301<br />
          Mob: 93219 07774 &nbsp;|&nbsp; Phone: 02953-232681<br />
          Email: patidarsamaj_mumbai@yahoo.com &nbsp;|&nbsp; Website: www.patidarsevasamaj.org<br />
          GSTIN: 08AAATS2084A1Z1 &nbsp;|&nbsp; PAN: AAATS2084A
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Invoice Meta                                                    */}
      {/* ---------------------------------------------------------------- */}
      <section className="py-2  mb-3">
        <div className="row">
          <div className="col-6">
            <table className="table table-bordered table-sm mb-0">
              <tbody>
                <tr>
                  <td className="py-1">Invoice No</td>
                  <td className="text-end fw-semibold py-1">{invoiceData?.invoice.invoice_no}</td>
                </tr>
                <tr>
                  <td className=" py-1">Booking No</td>
                  <td className="text-end fw-semibold py-1">{invoiceData?.invoice.booking_no}</td>
                </tr>
                <tr>
                  <td className=" py-1">Guest ID</td>
                  <td className="text-end fw-semibold py-1">{invoiceData?.invoice.guest_id}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-6">
            <table className="table table-bordered table-sm mb-0">
              <tbody>
                <tr>
                  <td className=" py-1">Invoice Date</td>
                  <td className="text-end fw-semibold py-1">{formatValue(invoiceData.invoice.invoice_date, "date")}</td>
                </tr>
                <tr>
                  <td className=" py-1">Payment Status</td>
                  <td className="text-end fw-semibold py-1">{invoiceData.invoice.payment_status}</td>
                </tr>
                <tr>
                  <td className=" py-1">Check In</td>
                  <td className="text-end fw-semibold py-1">{formatValue(invoiceData.invoice.check_in, "date")}</td>
                </tr>
                <tr>
                  <td className=" py-1">Check Out</td>
                  <td className="text-end fw-semibold py-1">{formatValue(invoiceData.invoice.check_out, "date")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="d-flex justify-content-between border-bottom py-2 mt-1 small">
          <span>
            Stay Duration:{" "}
            <span className="text-dark fw-semibold">
              {invoiceData.invoice.total_nights}{" "}
              {invoiceData.invoice.total_nights > 1 ? "Nights" : "Night"}
            </span>
          </span>
          <span>
            Booking Source: <span className="text-dark fw-semibold">{invoiceData.invoice.booking_source}</span>
          </span>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Guest Details                                                   */}
      {/* ---------------------------------------------------------------- */}
      <section className="border-bottom mb-3">
        <div className="row">
          <div className="col-6">
            <table className="table table-borderless table-sm mb-0">
              <tbody>
                <tr>
                  <td className=" py-1 text-nowrap">Guest Name : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.name}</td>
                </tr>
                <tr>
                  <td className=" py-1 text-nowrap">Mobile : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.phone}</td>
                </tr>
                <tr>
                  <td className=" py-1 text-nowrap">Email : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.email}</td>
                </tr>
                <tr>
                  <td className=" py-1 text-nowrap">Address : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.address}</td>
                </tr>
                {invoiceData.guest.gst_no && (
                  <tr>
                    <td className=" py-1 text-nowrap">GST Number : </td>
                    <td className="text-end fw-semibold py-1">{invoiceData.guest.gst_no}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="col-6">
            <table className="table table-borderless table-sm mb-0">
              <tbody>
                <tr>
                  <td className=" py-1">Adults : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.adults}</td>
                </tr>
                <tr>
                  <td className=" py-1">Children : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.children}</td>
                </tr>
                <tr>
                  <td className=" py-1">Total Guests : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.total_guests}</td>
                </tr>
                <tr>
                  <td className=" py-1">Total Rooms : </td>
                  <td className="text-end fw-semibold py-1">{invoiceData.guest.room_count}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Charges Table                                                   */}
      {/* ---------------------------------------------------------------- */}
      <section className="py-2 mb-3">
        <table className="table table-bordered table-sm table-striped mb-0">
          <thead className="table-light">
            <tr>
              <th>Description</th>
              <th className="text-center">Billing Period</th>
              <th className="text-end">Qty</th>
              <th className="text-center">Unit</th>
              <th className="text-end">Rate</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {displayCharges.map((item, idx) => (
              <tr key={idx}>
                <td>{item.description}</td>
                <td className="text-center">{item.period}</td>
                <td className="text-end">{item.qty}</td>
                <td className="text-center">{item.unit}</td>
                <td className="text-end">{item.rate}</td>
                <td className="text-end">{item.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Summary                                                         */}
      {/* ---------------------------------------------------------------- */}
      <section className="row mt-3">
        <div className="col-6">
          <div className="mb-1">
            <div className="mb-1 small">
              Amount in Words : <span className="fw-semibold">{invoiceData.invoice.amount_in_words}</span>{" "}
            </div>
          </div>
          <div className="border-top pt-2">
            <div className="fw-bold mb-1">Terms &amp; Conditions</div>
            <ul className="ps-3 mb-0 small text-muted">
              <li>Booking Will Not be Cancelled</li>
              <li>Subject to local jurisdiction.</li>
              <li>This is a computer-generated invoice and requires no physical signature.</li>
              <li>Thank you for staying with us.</li>
            </ul>
          </div>
        </div>

        <div className="col-6">
          <table className="table table-borderless table-sm mb-0">
            <tbody>
              {roomChargesWithParking > 0 && (
                <tr>
                  <td className="py-1">Room Charges</td>
                  <td className="text-end py-1">{formatCurrency(roomChargesWithParking)}</td>
                </tr>
              )}
              {parseFloat(invoiceData.summary.extra_charges_total.replace(/[^\d.]/g, "")) > 0 && (
                <tr>
                  <td className="py-1">Extra Charges</td>
                  <td className="text-end py-1">{invoiceData.summary.extra_charges_total}</td>
                </tr>
              )}
              {parseFloat(invoiceData.summary.restaurant_charges_total.replace(/[^\d.]/g, "")) > 0 && (
                <tr>
                  <td className="py-1">Restaurant</td>
                  <td className="text-end py-1">{invoiceData.summary.restaurant_charges_total}</td>
                </tr>
              )}
              {parseFloat(invoiceData.summary.laundry_charges_total.replace(/[^\d.]/g, "")) > 0 && (
                <tr>
                  <td className="py-1">Laundry</td>
                  <td className="text-end py-1">{invoiceData.summary.laundry_charges_total}</td>
                </tr>
              )}
              {parseFloat(invoiceData.summary.service_charge.replace(/[^\d.]/g, "")) > 0 && (
                <tr>
                  <td className="py-1">Service Charge</td>
                  <td className="text-end py-1">{invoiceData.summary.service_charge}</td>
                </tr>
              )}
              <tr className="border-top">
                <td className="fw-bold py-1">Taxable Amount</td>
                <td className="text-end fw-bold py-1">{invoiceData.summary.taxable_amount}</td>
              </tr>

              {/* ---- dynamic tax rows: exactly as many as invoiceData.taxes has ---- */}
              {invoiceData.taxes.map((tax, idx) => (
                <tr key={idx}>
                  <td className="py-1">{tax.name} ({tax.percent}%)</td>
                  <td className="text-end py-1">{tax.amount}</td>
                </tr>
              ))}

              {invoiceData.round_off && parseFloat(invoiceData.round_off.replace(/[^\d.-]/g, "")) !== 0 && (
                <tr className="text-muted small">
                  <td className="py-1">Round Off</td>
                  <td className="text-end py-1">{invoiceData.round_off}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="d-flex justify-content-between align-items-center border-top  px-2 py-2 fw-bold">
            <span>Grand Total</span>
            <span>{invoiceData.invoice.grand_total}</span>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Signatures                                                      */}
      {/* ---------------------------------------------------------------- */}
      <footer className="row mt-5 pt-4">
        <div className="col-4 text-center">
          <div className="border-top pt-2">Guest Signature</div>
        </div>
        <div className="col-4 text-center">
          <div className="border-top pt-2">Cashier</div>
        </div>
        <div className="col-4 text-center">
          <div className="border-top pt-2">Authorized Signatory</div>
        </div>
      </footer>
    </div>
  );
};

export default CheckOutPrintInvoice;